import { Button } from 'primereact/button';
import { Menu } from 'primereact/menu';
import { useEffect, useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import ErrorMessage from '../../../../../../../../../../components/error-message';
import { PROCESS_DROPDOWN_OPTIONS } from '../../PcfWizard.type';
import { getProcessDetailsBasedOnProcessId } from './helpers';

const AddProcessButton = (): JSX.Element | null => {
  const { t } = useTranslation();

  const processLabelMap: Record<PROCESS_DROPDOWN_OPTIONS, string> = {
    [PROCESS_DROPDOWN_OPTIONS.INJECTION_MOLDING]: t(
      'calculationWizard.sections.manufacturingProcessSection.injectionMoldingProcessLabel',
    ),
    [PROCESS_DROPDOWN_OPTIONS.INDIVIDUAL_PROCESS]: t(
      'calculationWizard.sections.manufacturingProcessSection.individualProcessLabel',
    ),
    [PROCESS_DROPDOWN_OPTIONS.TRANSPORT_PROCESS]: t(
      'calculationWizard.sections.manufacturingProcessSection.transportProcessLabel',
    ),
  };

  const { setValue, control, formState } = useFormContext();
  const buttonLabel = t('calculationWizard.sections.manufacturingProcessSection.addProcessButton');
  const processes = useWatch({
    name: 'processes',
    control,
  });

  const menuRef = useRef<Menu>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [syncedWidth, setSyncedWidth] = useState<number>();

  useEffect(() => {
    // Reset width on label change (e.g language switch) to allow natural resizing.
    setSyncedWidth(undefined);
  }, [buttonLabel]);

  /**
   * Calculates a single width that will be shared by both
   * the Button and the popup Menu
   */
  const syncWidths = () => {
    const buttonWidth = buttonRef.current?.offsetWidth ?? 0;
    requestAnimationFrame(() => {
      const menuRoot = menuRef.current?.getElement();
      const menuWidth = menuRoot?.getBoundingClientRect().width ?? 0;

      setSyncedWidth(Math.max(buttonWidth, menuWidth));
    });
  };

  const onClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    menuRef?.current?.toggle(event);
  };

  const menuItems = Object.values(PROCESS_DROPDOWN_OPTIONS).map((item) => {
    return {
      id: item,
      label: processLabelMap[item],
      command: () => {
        return onMenuItemClick(item);
      },
    };
  });

  const onMenuItemClick = async (selectedValue: PROCESS_DROPDOWN_OPTIONS) => {
    if (selectedValue) {
      const newProcesses = [...processes];
      const processDetails = getProcessDetailsBasedOnProcessId(
        selectedValue,
        newProcesses.length + 1,
      );
      if (processDetails) {
        newProcesses.push(processDetails);
      }
      setValue('processes', newProcesses);
    }
  };

  return (
    <div>
      <Menu
        ref={menuRef}
        popup
        model={menuItems}
        onShow={syncWidths}
        style={{ width: syncedWidth ? `${syncedWidth}px` : 'auto' }}
      />

      <Button
        label={buttonLabel}
        icon='pi pi-plus-circle'
        onClick={onClick}
        style={{ width: syncedWidth ? `${syncedWidth}px` : 'auto' }}
        pt={{
          root: {
            ref: buttonRef,
          },
        }}
      />
      {/* Error Message Block for Process Required Error Message */}
      {formState?.errors['processes'] && (
        <div className='py-2'>
          <ErrorMessage
            message={
              typeof formState.errors['processes']?.message === 'string'
                ? formState.errors['processes']?.message
                : ''
            }
          />
        </div>
      )}
    </div>
  );
};

export default AddProcessButton;
